import { Shield, FileText, Users, AlertTriangle, Mail, Phone, ChevronDown, ChevronUp, Download } from "lucide-react";
import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const SafeguardingPage = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  const principles = [
    {
      title: "Dignità e Rispetto",
      description: "Tutti i Tesserati e i Soci hanno il diritto fondamentale di essere trattati con dignità e rispetto."
    },
    {
      title: "Salute e Benessere",
      description: "La salute e il benessere psicofisico dei Tesserati sono valori prioritari, che superano l'importanza dei risultati sportivi."
    },
    {
      title: "Ambiente Sicuro e Inclusivo",
      description: "Creare un ambiente sano, sicuro e inclusivo per tutti i tesserati, promuovendo fair play e rispetto reciproco."
    },
    {
      title: "Protezione dei Minori",
      description: "La tutela dei minori è una priorità assoluta della nostra Società, con particolare attenzione alla prevenzione di ogni forma di abuso."
    },
  ];

  const sections = [
    {
      title: "Finalità del Modello",
      content: `Il presente Modello ha lo scopo di:
• Prevenire e contrastare ogni forma di abuso, molestia, violenza di genere o discriminazione
• Garantire la tutela della dignità e del benessere di tutti i tesserati
• Promuovere un ambiente sportivo sano, sicuro e inclusivo
• Definire procedure chiare per la gestione delle segnalazioni
• Assicurare una gestione etica e trasparente all'interno della Società`
    },
    {
      title: "Ambito di Applicazione",
      content: `Il Modello si applica a:
• Tutti i Tesserati della Società (atleti, tecnici, dirigenti)
• Soci e volontari
• Collaboratori retribuiti e non
• Qualsiasi persona che partecipi alle attività organizzate dalla Società
• Genitori e accompagnatori durante le attività sportive`
    },
    {
      title: "Diritti dei Tesserati",
      content: `Ogni tesserato ha diritto a:
• Essere trattato con rispetto e dignità in ogni circostanza
• Praticare sport in un ambiente sicuro e protetto
• Essere tutelato da ogni forma di abuso, violenza o discriminazione
• Ricevere informazioni chiare sulle politiche di safeguarding
• Effettuare segnalazioni in modo riservato e protetto
• Non subire ritorsioni in caso di segnalazione in buona fede`
    },
    {
      title: "Tutela dei Minori",
      content: `Per la protezione dei minori, la Società adotta le seguenti misure:
• Richiesta del certificato del Casellario Giudiziale per tutti i soggetti a contatto con minori
• Divieto di affidamento di minori a soggetti con precedenti per reati contro i minori
• Formazione specifica per allenatori e accompagnatori
• Supervisione costante durante le attività sportive
• Protocolli specifici per trasferte e ritiri`
    },
    {
      title: "Condotte Vietate",
      content: `Sono espressamente vietate le seguenti condotte:
• Abuso sessuale e molestie di qualsiasi natura
• Violenza fisica e psicologica
• Bullismo e cyberbullismo
• Comportamenti discriminatori basati su etnia, religione, disabilità, genere, orientamento sessuale
• Negligenza e incuria nella gestione dei minori
• Qualsiasi comportamento che comprometta la dignità della persona`
    },
    {
      title: "Sistema Sanzionatorio",
      content: `In caso di violazione del presente Modello, sono previste sanzioni proporzionate alla gravità della condotta:

Per collaboratori retribuiti:
• Richiamo verbale o scritto
• Sospensione temporanea
• Risoluzione del rapporto di lavoro

Per volontari, tesserati e associati:
• Ammonizione
• Sospensione temporanea dall'attività
• Esclusione definitiva dalla Società

In caso di condotte penalmente rilevanti, è prevista la segnalazione alle autorità competenti.`
    },
  ];

  return (
    <div data-testid="safeguarding-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-12 bg-[#0047AB] noise-overlay">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-6">
            <Shield className="w-10 h-10" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-black uppercase mb-4 animate-fade-in-up">
            Safeguarding
          </h1>
          <p className="text-blue-200 max-w-2xl mx-auto">
            Modello di Organizzazione e Gestione per la tutela dei minori e la prevenzione delle molestie, della violenza di genere e di ogni altra condizione di discriminazione
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-blue-50 border-l-4 border-[#0047AB] p-6 rounded-r-xl">
            <h2 className="text-lg font-bold text-slate-900 mb-2">
              Cos'è il Safeguarding?
            </h2>
            <p className="text-slate-600">
              Il Safeguarding è l'insieme di politiche, procedure e pratiche adottate per garantire un ambiente sportivo sicuro, 
              rispettoso e inclusivo per tutti i partecipanti, con particolare attenzione alla protezione dei minori. 
              In conformità alle disposizioni della FIGC e del D.Lgs. 39/2021, il G.S.D. Volpiano Pianese ha adottato 
              il presente Modello di Organizzazione e Gestione.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2">
              I Nostri Principi
            </h2>
            <div className="w-20 h-1 bg-[#0047AB] mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {principles.map((principle, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#0047AB]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{principle.title}</h3>
                <p className="text-sm text-slate-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsabile Safeguarding */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#0047AB] to-[#003380] rounded-2xl p-8 text-white">
            <div className="flex items-start gap-6">
              <div className="hidden md:flex w-16 h-16 bg-white/20 rounded-full items-center justify-center flex-shrink-0">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Responsabile Safeguarding</h2>
                <p className="text-blue-200 mb-4">
                  Il Responsabile Safeguarding è la figura designata dalla Società per garantire l'applicazione 
                  delle politiche di tutela, ricevere le segnalazioni e coordinare le attività di prevenzione.
                </p>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-sm mb-2">
                    Per informazioni o segnalazioni, contattare:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a 
                      href="mailto:segreteria@gsdvolpiano.it" 
                      className="flex items-center gap-2 text-white hover:text-blue-200 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">segreteria@gsdvolpiano.it</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accordion Sections */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2">
              Il Modello in Dettaglio
            </h2>
            <div className="w-20 h-1 bg-[#0047AB] mx-auto rounded-full"></div>
          </div>

          <div className="space-y-4">
            {sections.map((section, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900">{section.title}</span>
                  {openSection === index ? (
                    <ChevronUp className="w-5 h-5 text-[#0047AB]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                {openSection === index && (
                  <div className="px-6 pb-4">
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-slate-600 whitespace-pre-line text-sm leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Segnalazioni */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Come Effettuare una Segnalazione
                </h3>
                <p className="text-slate-600 mb-4">
                  Chiunque sia a conoscenza o abbia il sospetto di comportamenti in violazione del presente Modello 
                  è tenuto a segnalarlo tempestivamente. Le segnalazioni possono essere effettuate:
                </p>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-[#0047AB] font-bold">•</span>
                    <span>Al Responsabile Safeguarding della Società via email a <a href="mailto:segreteria@gsdvolpiano.it" className="text-[#0047AB] hover:underline">segreteria@gsdvolpiano.it</a></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#0047AB] font-bold">•</span>
                    <span>Alla Commissione Federale per la Safeguarding FIGC (contatti sul sito ufficiale FIGC)</span>
                  </li>
                </ul>
                <p className="text-sm text-slate-500 mt-4">
                  <strong>Tutela del segnalante:</strong> È garantita la massima riservatezza e la protezione da qualsiasi forma di ritorsione per chi effettua segnalazioni in buona fede.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download PDF */}
      <section className="py-12 bg-[#0047AB]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <FileText className="w-12 h-12 text-white/80 mx-auto mb-4" />
          <h3 className="text-xl lg:text-2xl font-bold text-white mb-4">
            Scarica il Modello Completo
          </h3>
          <p className="text-blue-200 mb-6">
            Consulta il documento integrale del Modello di Organizzazione e Gestione per la Safeguarding
          </p>
          <a
            href="https://customer-assets.emergentagent.com/job_fef51c73-3fe9-4a0a-b70a-2a7119fa5032/artifacts/wvl4spbe_MODELLO%20SAFEGUARDG.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-[#0047AB] font-semibold px-8 py-3 rounded-full hover:bg-slate-100 transition-colors"
          >
            <Download className="w-5 h-5" />
            Scarica PDF
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SafeguardingPage;
